/**
 * WebSocket Real-Time Sync Client
 * 
 * Connects to backend Socket.IO server for instant CRM updates.
 * Handles: bookings, properties, leads, sync events
 */

import { io } from 'socket.io-client';

// Get backend URL from environment
// iter152 — Never fall back to a hardcoded preview URL. Same bug class as
// the Android release regression (iter147): a preview URL baked into the
// production bundle. If REACT_APP_BACKEND_URL is missing at build time the
// module fails loudly so CI catches it before the bundle ships.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL is not defined. Refusing to run — this would silently point the app at an unknown backend.');
}

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.listeners = new Map();
    this.userPhone = null;
    this.propertyId = null;
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    if (this.socket?.connected) {
      console.log('[WebSocket] Already connected');
      return this.socket;
    }

    console.log('[WebSocket] Connecting to:', BACKEND_URL);

    this.socket = io(BACKEND_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,   // never give up — phone networks blip a lot
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      timeout: 20000,
    });

    // Browser came back online → force an immediate reconnect attempt
    if (typeof window !== 'undefined' && !this._onlineHandlerBound) {
      this._onlineHandlerBound = true;
      window.addEventListener('online', () => {
        if (this.socket && !this.socket.connected) {
          console.log('[WebSocket] Network online — reconnecting');
          this.socket.connect();
        }
      });
    }

    this.setupEventHandlers();

    return this.socket;
  }

  /**
   * Setup connection event handlers
   */
  setupEventHandlers() {
    // Connection established
    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected | Socket ID:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // Re-subscribe to rooms if we have user info
      if (this.userPhone) {
        this.subscribeToUser(this.userPhone);
      }
      if (this.propertyId) {
        this.subscribeToProperty(this.propertyId);
      }

      // Notify listeners
      this.notifyListeners('connection', { status: 'connected', socketId: this.socket.id });
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.reconnectAttempts++;
      this.notifyListeners('connection', { status: 'error', error: error.message });
    });

    // Disconnected
    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.isConnected = false;
      this.notifyListeners('connection', { status: 'disconnected', reason });
    });

    // Welcome message from server
    this.socket.on('connected', (data) => {
      console.log('[WebSocket] Server welcome:', data.message);
    });

    // CRM update events
    this.socket.on('crm_update', (payload) => {
      console.log('[WebSocket] CRM Update:', payload.type, payload);
      this.notifyListeners('crm_update', payload);
    });

    // Booking update events
    this.socket.on('booking_update', (payload) => {
      console.log('[WebSocket] Booking Update:', payload.type, payload);
      this.notifyListeners('booking_update', payload);
    });

    // Property update events
    this.socket.on('property_update', (payload) => {
      console.log('[WebSocket] Property Update:', payload.type, payload);
      this.notifyListeners('property_update', payload);
    });

    // Lead update events
    this.socket.on('lead_update', (payload) => {
      console.log('[WebSocket] Lead Update:', payload.type, payload);
      this.notifyListeners('lead_update', payload);
    });

    // Sync complete events
    this.socket.on('sync_complete', (payload) => {
      console.log('[WebSocket] Sync Complete:', payload.sync_type, payload);
      this.notifyListeners('sync_complete', payload);
    });

    // Subscription confirmations
    this.socket.on('subscribed', (data) => {
      console.log('[WebSocket] Subscribed to:', data.room);
    });

    // Error events
    this.socket.on('error', (data) => {
      console.error('[WebSocket] Error:', data.message);
      this.notifyListeners('error', data);
    });
  }

  /**
   * Subscribe to user-specific updates (by phone)
   */
  subscribeToUser(phone) {
    if (!this.socket?.connected) {
      this.userPhone = phone; // Store for reconnection
      return;
    }

    const normalizedPhone = phone.replace(/\D/g, '').slice(-10);
    this.userPhone = normalizedPhone;
    
    this.socket.emit('subscribe_user', { phone: normalizedPhone });
    console.log('[WebSocket] Subscribing to user:', normalizedPhone);
  }

  /**
   * Subscribe to property-specific updates
   */
  subscribeToProperty(propertyId) {
    if (!this.socket?.connected) {
      this.propertyId = propertyId; // Store for reconnection
      return;
    }

    this.propertyId = propertyId;
    this.socket.emit('subscribe_property', { property_id: propertyId });
    console.log('[WebSocket] Subscribing to property:', propertyId);
  }

  /**
   * Unsubscribe from property updates
   */
  unsubscribeFromProperty(propertyId) {
    if (!this.socket?.connected) return;
    
    this.socket.emit('unsubscribe_property', { property_id: propertyId });
    console.log('[WebSocket] Unsubscribing from property:', propertyId);
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    this.listeners.get(event)?.delete(callback);
  }

  /**
   * Notify all listeners for an event
   */
  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('[WebSocket] Listener error:', error);
        }
      });
    }
  }

  /**
   * Disconnect from server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('[WebSocket] Manually disconnected');
    }
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts,
      userPhone: this.userPhone,
      propertyId: this.propertyId,
    };
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;

// Named exports for convenience
export const connectWebSocket = () => websocketService.connect();
export const disconnectWebSocket = () => websocketService.disconnect();
export const subscribeToUser = (phone) => websocketService.subscribeToUser(phone);
export const subscribeToProperty = (propertyId) => websocketService.subscribeToProperty(propertyId);
export const onWebSocketEvent = (event, callback) => websocketService.on(event, callback);
export const offWebSocketEvent = (event, callback) => websocketService.off(event, callback);
export const getWebSocketStatus = () => websocketService.getStatus();
